import { animate, state, style, transition, trigger } from '@angular/animations';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, computed, DestroyRef, OnInit, signal, WritableSignal } from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { FormControl } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatTableDataSource } from '@angular/material/table';
import { NavigationEnd, Router } from '@angular/router';
import { DARequest } from '@dr-customer-offers-ui/lib-interfaces';
import { Store } from '@ngrx/store';
import {
  BulkType,
  DataModel,
  DetailTableCellModel,
  NgxIntervalDataGridRowModel,
  NgxIntervalDataGridService,
  TableDataTypes,
  Week,
  WeekDays
} from '@ngx-interval-data-grid';
import moment from 'moment';
import { filter, Observable, of, take, tap } from 'rxjs';
import { BulkInput, InputTypeEnum, OffersDataInterface, TabOption } from '../../../shared/models';
import { UIState } from '../../../shared/models/utility';
import { DataModelService } from '../../../shared/services/data-model.service';
import { DataViewModelService } from '../../../shared/services/data-vm.service';
import { DateService } from '../../../shared/services/date.service';
import { InternalService } from '../../../shared/services/internal.service';
import { MixPanelService } from '../../../shared/services/mixpanel.service';
import { AppState } from '../../../store';
import * as Actions from '../../../store/actions/com.actions';
import * as Selectors from '../../../store/selectors/com.selectors';
import { ReasonCodeConfirmComponent } from '../../dialogs/reason-code-confirm/reason-code-confirm.component';
import { FiltersStateService } from '../../filters/filters-state.service';

@Component({
  selector: 'dr-customer-offers-ui-da-table',
  templateUrl: './da-tab-table.component.html',
  styleUrls: ['./da-tab-table.component.scss'],
  animations: [
    trigger('detailExpand', [
      state('collapsed', style({ height: '0px', minHeight: '0' })),
      state('expanded', style({ height: '*' })),
      transition('expanded <=> collapsed', animate('150ms cubic-bezier(0.4, 0.0, 0.2, 1)'))
    ])
  ],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class DeclaredAvailabilityTabTableComponent implements OnInit {
  protected daOffersLoading = toSignal(this.store.select(Selectors.selectDAOffersLoading), { initialValue: false });
  protected currentRegViewConfig = toSignal(this.store.select(Selectors.selectCurrentRegViewConfig), { initialValue: null });

  UIState = UIState;
  protected daData: NgxIntervalDataGridRowModel[] | null = null;
  protected disableConfirmButton$: Observable<boolean> = this.ngxService.getDisableSave();
  protected editModeSignal: WritableSignal<boolean> = signal(false);
  protected isGroupedOffersSignal = this.internalService.isGroupedOffersSignal;
  protected minDate!: moment.Moment;
  protected maxDate!: moment.Moment;
  protected applyButtonDisabled: WritableSignal<boolean> = this.filtersStateService.isApplyButtonDisabledSignal;
  private selectedRegControl: FormControl = this.dataVMService.selectedRegFormControl;
  protected displaySelectedReg = '';
  protected dateLabel$: Observable<string | null> = of(null);

  userLocale = computed(() => this.internalService.userPreferenceSignal()?.locale ?? 'en-US');

  protected dates: number[] = [];
  protected timezoneAbbr!: string;

  // Define column headers and fields
  columns: string[] = WeekDays;
  displayedColumns: string[] = ['offer', 'time_period', ...this.columns];
  serverData!: NgxIntervalDataGridRowModel[];
  public dataSource = new MatTableDataSource<NgxIntervalDataGridRowModel>(this.serverData);

  constructor(
    private store: Store<AppState>,
    private ngxService: NgxIntervalDataGridService,
    private internalService: InternalService,
    private cdr: ChangeDetectorRef,
    private dataModelService: DataModelService,
    private dataVMService: DataViewModelService,
    private mixPanelService: MixPanelService,
    public dialog: MatDialog,
    private router: Router,
    private destroyRef: DestroyRef,
    private filtersStateService: FiltersStateService,
    private dateService: DateService
  ) {}

  ngOnInit(): void {
    this.store
      .select(Selectors.selectCurrentDAOffers)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        filter((offers) => offers !== null)
      )
      .subscribe((offers: OffersDataInterface | null) => {
        if (offers) this.processGroupedData(offers);
        this.filtersStateService.setApplyButtonChanging(this.dataVMService.selectedRegFormControl.value);
      });

    this.loadData();

    this.router.events
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        filter((event) => event instanceof NavigationEnd)
      )
      .subscribe(() => {
        this.loadData();
      });

    this.mixPanelService.viewTab('Open');
    this.applyBulk();
  }

  get showBulkOffers(): boolean {
    return this.dataVMService.selectedTabOptionSignal?.()?.bulkFunctionality && this.editModeSignal?.() && !this.isGroupedOffersSignal?.();
  }

  get selectedTabSignal(): WritableSignal<TabOption> {
    return this.dataVMService.selectedTabOptionSignal;
  }

  triggerEditMode(isEditMode: boolean): void {
    this.editModeSignal.set(isEditMode);
  }

  private loadData(): void {
    this.ngxService.clearUnSavedData();

    const selectedDates = this.dataVMService.getAppliedWeekRange();
    const regConfig = this.currentRegViewConfig();

    if (!regConfig || !selectedDates || !selectedDates.start || !selectedDates.end || this.selectedRegControl.value === null) {
      console.warn('Missing required data for loading offers');
      return;
    }

    if (regConfig && !regConfig.graph_view && this.selectedRegControl.value.id) {
      const selectedDatesISO = this.dateService.getISODate(selectedDates, regConfig.timeZone);

      // We need this setTimeOut to make sure we do not dispatch loadDAOffers with previous filter values especially start and end date.
      setTimeout(() => {
        this.store.dispatch(
          Actions.loadDAOffers({
            registrationId: regConfig.registrationId,
            startDate: selectedDatesISO.start,
            endDate: selectedDatesISO.end
          })
        );
      }, 10);
    }

    this.displaySelectedReg = regConfig.display_labels || '';
  }

  /**
   * This method is updated each time you enter a value on any of the input field
   * @param element provides the whole element which is being updated
   * @param event is an KeyboardEvent where we get the value entered.
   * @param week is used to provide the week which we are updating the value for.
   */
  inputToCellValue(element: NgxIntervalDataGridRowModel, inputEvent: FocusEvent, week: string) {
    const target = inputEvent?.target as HTMLInputElement | null;
    const value: number | null = target?.valueAsNumber === undefined || isNaN(target.valueAsNumber) ? null : target?.valueAsNumber;
    const oldData: NgxIntervalDataGridRowModel = this.ngxService
      .getUntouchedDataFromServer()
      .tableNgxIntervalData[TableDataTypes.DECLARED_AVAILABILITIES].find(
        (f) => f.timePeriodKey === element.timePeriodKey
      ) as NgxIntervalDataGridRowModel;
    const nullReset = oldData[week as Week].value === null && value === null;
    const isValid =
      nullReset ||
      (value !== null &&
        value <= (this.currentRegViewConfig()?.maximumValue ?? 0) &&
        (value === 0 || value >= (this.currentRegViewConfig()?.minimumValue ?? 0)));
    if (value && this.currentRegViewConfig()?.gateRules?.full_day_offer_value) {
      this.serverData = [
        ...this.ngxService.updateValueBulk(BulkType.DAY, value, TableDataTypes.DECLARED_AVAILABILITIES, week as Week, undefined, undefined, !isValid)
      ];
      this.dataSource.data = this.serverData;
      this.cdr.detectChanges();
    } else {
      const updatedDataElement = this.ngxService.updateElementValue({
        weekName: week as Week,
        timePeriodKey: element.timePeriodKey,
        value,
        invalid_value: !isValid,
        tableDataType: TableDataTypes.DECLARED_AVAILABILITIES
      });
      this.serverData = [...updatedDataElement];
      this.dataSource = new MatTableDataSource<NgxIntervalDataGridRowModel>(this.serverData);
      this.cdr.detectChanges();
    }
  }

  /**
   * This method is used when user cancels from edit mode. Here we forget all his previous updates and take data we recieved from the server.
   */
  cancel() {
    const data: DataModel[] = this.ngxService.getUntouchedDataFromServer().dataModelRef[TableDataTypes.DECLARED_AVAILABILITIES] as DataModel[];
    this.ngxService.clearUnSavedData();
    this.editModeSignal.set(false);
    this.dataSource.data = this.ngxService.groupByDayOfWeek(
      data,
      this.currentRegViewConfig()?.timeZone ?? 'UTC',
      TableDataTypes.DECLARED_AVAILABILITIES
    );
  }

  /**
   * This is the main method used to save the updated data and switch back to the readonly mode
   */

  save() {
    const hasReasons =
      this.currentRegViewConfig()?.gateRules &&
      Array.isArray(this.currentRegViewConfig()?.gateRules.post_gate_edits.post_gate_change_reasons) &&
      this.currentRegViewConfig()!.gateRules.post_gate_edits.post_gate_change_reasons.length > 0;
    const provideChangeReason = this.currentRegViewConfig()?.gateRules.post_gate_edits.provide_change_reason;

    // Check if change reason should be provided:
    if (provideChangeReason && hasReasons) {
      const dialogRef = this.dialog.open(ReasonCodeConfirmComponent, {
        data: {
          postGateChangeReasons: this.currentRegViewConfig()?.gateRules.post_gate_edits.post_gate_change_reasons
        },
        width: '24.375rem',
        disableClose: false,
        hasBackdrop: true
      });

      dialogRef
        .afterClosed()
        .pipe(
          tap((reason: string) => {
            if (reason) {
              const unsavedOffers: DetailTableCellModel[] = this.dataVMService.lookForDSTDuplication(
                this.ngxService.unsavedData,
                this.currentRegViewConfig()?.intervalFrequency
              );
              const daOffersToPost: DARequest[] = this.dataModelService.getDAOffersToPost(unsavedOffers, this.currentRegViewConfig(), reason);
              this.postOffers(daOffersToPost);
            } else {
              this.cancel();
            }
          }),
          take(1)
        )
        .subscribe();
    } else {
      const unsavedOffers: DetailTableCellModel[] = this.dataVMService.lookForDSTDuplication(
        this.ngxService.unsavedData,
        this.currentRegViewConfig()?.intervalFrequency
      );
      const daOffersToPost: DARequest[] = this.dataModelService.getDAOffersToPost(unsavedOffers, this.currentRegViewConfig());
      this.postOffers(daOffersToPost);
    }
  }

  postOffers(offers: DARequest[]) {
    const selectedDates = this.dataVMService.getAppliedWeekRange();
    const regConfig = this.currentRegViewConfig();

    if (regConfig && selectedDates) {
      const selectedDatesISO = this.dateService.getISODate(selectedDates, regConfig.timeZone);
      this.store.dispatch(
        Actions.postDAOffers({ offers, registrationId: regConfig.registrationId, startDate: selectedDatesISO.start, endDate: selectedDatesISO.end })
      );
    }
  }

  applyBulk(): void {
    this.internalService.getBulkInputs$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((b: BulkInput | null) => {
      if (!b) return;
      switch (b.bulkType) {
        case BulkType.CUSTOM:
          {
            if (b.inputType === InputTypeEnum.VALUE && b.value !== null && b.value !== undefined)
              this.serverData = [
                ...this.ngxService.updateValueBulk(
                  BulkType.CUSTOM,
                  b.value,
                  TableDataTypes.DECLARED_AVAILABILITIES,
                  undefined,
                  moment(b.startDateAndTime),
                  moment(b.endDateAndTime),
                  false,
                  b.operation
                )
              ];
          }
          break;
        case BulkType.DAY:
          if (b.value !== null && b.value !== undefined)
            this.serverData = [...this.ngxService.updateValueBulk(BulkType.DAY, b.value, TableDataTypes.DECLARED_AVAILABILITIES, b.week)];
          break;
        case BulkType.WEEK:
          if (b.value !== null && b.value !== undefined)
            this.serverData = [...this.ngxService.updateValueBulk(BulkType.WEEK, b.value, TableDataTypes.DECLARED_AVAILABILITIES)];
          break;
      }
      this.dataSource = new MatTableDataSource<NgxIntervalDataGridRowModel>(this.serverData);
      this.cdr.detectChanges();
    });
  }

  private processGroupedData(value: OffersDataInterface): void {
    const timeZone = this.currentRegViewConfig()?.timeZone ?? 'UTC';
    this.timezoneAbbr = moment.tz(this.currentRegViewConfig()?.timeZone ?? 'UTC').zoneName();
    this.dataSource.data = this.daData = value.values ?? [];
    const startDate = moment(this.dataSource.data[0].Sunday.start_date_time).tz(timeZone);

    this.dates = [];
    this.displayedColumns = value.values?.[0].groupedOffersIntervals ? ['time_period', ...this.columns] : ['offer', 'time_period', ...this.columns];

    for (let i = 0; i < 7; i++) {
      const newDate = startDate.clone().add(i, 'days');
      this.dates.push(newDate.date());
    }

    const start = moment(this.dataSource.data[0].Sunday.start_date_time).tz(timeZone).locale(this.userLocale()).format('L');
    const end = moment(this.dataSource.data[0].Saturday.start_date_time).tz(timeZone).locale(this.userLocale()).format('L');
    this.dateLabel$ = of(`${start} - ${end}`);

    this.editModeSignal.set(false);
    this.cdr.detectChanges();
  }
}
